import { Children, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const KEY = "feed-rotation-idx";
const MIN_H = 8;

// shows exactly one banner; each feed visit/refresh moves on to the next one that has something to show
export function FeedRotator({ children }) {
  const slots = Children.toArray(children).filter(Boolean);
  const n = slots.length;
  const [start] = useState(() => {
    const want = new URLSearchParams(window.location.search).get("slot");
    const i = want ? slots.findIndex((s) => s.key === `.$${want}`) : -1;
    return i >= 0 ? i : ((parseInt(localStorage.getItem(KEY), 10) || 0) + 1) % Math.max(n, 1);
  });
  const [cur, setCur] = useState(null);
  const [locked, setLocked] = useState(false);
  const refs = useRef([]);
  const filled = (i) => (refs.current[i]?.offsetHeight || 0) >= MIN_H;
  const pick = (from, dir = 1) => {
    for (let k = 0; k < n; k++) { const i = (from + dir * k + n * n) % n; if (filled(i)) return i; }
    return null;
  };

  useEffect(() => {
    if (locked || !n) return undefined;
    let ticks = 0;
    const iv = setInterval(() => {
      ticks += 1;
      if (filled(start)) { setCur(start); setLocked(true); return; }
      if (ticks >= 5) { setCur(pick(start)); setLocked(true); }
    }, 500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, n, start]);
  useEffect(() => { if (cur !== null) localStorage.setItem(KEY, String(cur)); }, [cur]);
  const { search } = useLocation();
  useEffect(() => {
    const want = new URLSearchParams(search).get("slot");
    const i = want ? slots.findIndex((s) => s.key === `.$${want}`) : -1;
    if (i >= 0) { setCur(i); setLocked(true); refs.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!n) return null;
  const go = (d) => setCur((c) => pick(((c ?? start) + d + n) % n, d) ?? c);
  return (
    <div className="relative" data-testid="feed-rotator" data-slot={cur === null ? "" : slots[cur]?.key}>
      {slots.map((el, i) => (
        <div key={el.key} ref={(r) => { refs.current[i] = r; }} aria-hidden={i !== cur}
          className={i === cur ? "feed-rot-in" : "absolute inset-x-0 top-0 invisible pointer-events-none -z-10 h-auto overflow-hidden"}>
          {el}
        </div>
      ))}
      {cur !== null && (
        <div className="mt-1.5 flex items-center justify-end gap-1">
          <button onClick={() => go(-1)} aria-label="Vorheriges" data-testid="feed-rotator-prev" className="w-7 h-7 rounded-full grid place-items-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-[11px] font-bold text-slate-400 tabular-nums" data-testid="feed-rotator-pos">{cur + 1}/{n}</span>
          <button onClick={() => go(1)} aria-label="Nächstes" data-testid="feed-rotator-next" className="w-7 h-7 rounded-full grid place-items-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}
